import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  Button,
  ButtonToolbar,
  Checkbox,
  DatePicker,
  FlexboxGrid,
  Form,
  SelectPicker,
} from "rsuite";

import "./OrderFilters.css";
import { updateIn } from "json-immut";
import { HelperServices } from "../../../core/services/HelperServices";
import { StoreServices } from "../../../core/services/StoreServices";
import { UserServices } from "../../../core/services/UserServices";
import { UtilityServices } from "../../../core/services/UtilityServices";
import { get } from "lodash/fp";
import { OrderListParams } from "./OrderDataTypes";
import {
  statusObjArr,
  orderDayTypeObj,
  roleTypeArr,
  messageTypeArr,
  orderStatusObjArr,
  dayTimeObjArr,
  operatorsObjArr,
  daysOffsetArr,
  orderServeAtObjArr,
  dateFormatObj,
  paymentModeObjArr,
  appConstObj,
} from "../../../core/config/AppConstants";
import { useTranslation } from "react-i18next";
import moment from "moment";

export interface PropTypesInterface {
  onSubmit: Function;
  onClear: Function;
  tabViewSelected: String;
}

export default function OrderFilters({
  onSubmit,
  onClear,
  tabViewSelected,
}: PropTypesInterface) {
  const { t } = useTranslation();
  const selectedCompany = useSelector((state: any) => state.company);
  const initSearchFormData = {
    order_no: "",
    item_name: "",
    customer: "",
    customer_name: "",
    customer_phone: "",
    company: "",
    store: "",
    order_serve_at: "",
    order_assigned_to: "",
    date_interval_type: "",
    date_interval_type_val: "",
    service_date_from: null,
    service_date_to: null,
    service_date: "",
    service_starts_at: "",
    served_by: "",
    order_status: "",
    payment_mode: "",
    is_order_rescheduled: "",
    rating_feedback_written_by: "",
    order_value: "",
    order_value_op: operatorsObjArr[0].value,
    discount_percent: "",
    discount_percent_op: operatorsObjArr[0].value,
    service_rating: "",
    service_rating_op: operatorsObjArr[0].value,
    building: "",
    area: "",
    city: "",
    state: "",
    country: "",
    pincode: "",
    status: "",
  };
  const [formData, setFormData] = useState<OrderListParams>(initSearchFormData);
  const [durationList, setDurationList] = useState([] as any);
  const [cityList, setCityList] = useState([]);
  const [stateList, setStateList] = useState([]);
  const [areaList, setAreaList] = useState([]);
  const [buildingList, setBuildingList] = useState([]);
  const [storeList, setStoreList] = useState([]);
  const [assignedToList, setAssignedToList] = useState([]);
  const [servedByList, setServedByList] = useState([]);
  const [showFilter, setShowFilter] = useState(false);
  const handleSearchToggle = () => setShowFilter(!showFilter);
  const countryList = useSelector((state: any) => state.country.data);

  const clearOrderDates = () => {
    formData.service_date_from = null;
    formData.service_date_to = null;
    setFormData({ ...formData });
  };

  const setOrderDates = (value: any) => {
    if (value == "custom") {
      clearOrderDates();
      return;
    }
    formData.service_date_from = new Date(
      moment()
        .add(daysOffsetArr[value as keyof typeof daysOffsetArr].offsetFrom, "d")
        .format(dateFormatObj.moment_y_m_d),
    );
    formData.service_date_to = new Date(
      moment()
        .add(daysOffsetArr[value as keyof typeof daysOffsetArr].offsetTo, "d")
        .format(dateFormatObj.moment_y_m_d),
    );
    setFormData({ ...formData });
  };

  async function storeSearch(searchStore: string, forComp?: number) {
    let compId: number = getCompanyId(forComp);

    if (!compId) {
      return;
    }
    try {
      const res = await StoreServices.getStores({
        company: compId,
        smart_search: searchStore,
      });
      const respData = get("data.data", res);
      if (respData.length) {
        setStoreList(respData);
      }
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error.message,
        type: messageTypeArr.error,
      });
    }
  }

  function getCompanyId(forComp?: number) {
    let compId: number = 0;
    if (UtilityServices.getLoggedInUser("user_type") == roleTypeArr.client) {
      compId = UtilityServices.getLoggedInUser("company_id");
    } else if (
      UtilityServices.getLoggedInUser("user_type") == roleTypeArr.backend
    ) {
      compId = forComp ? forComp : selectedCompany.id;
    }
    return compId;
  }

  async function assignedToSearch(searchStaff: string, forComp?: number) {
    let compId: number = getCompanyId(forComp);
    if (!compId) {
      return;
    }
    try {
      const res = await UserServices.getStaffList({
        company: compId.toString(),
        smart_search: searchStaff,
        rows: appConstObj.record_per_page_1000,
      });
      const respData = get("data.data", res);
      if (respData.length) {
        setAssignedToList(respData);
      }
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error.message,
        type: messageTypeArr.error,
      });
    }
  }

  async function servedBySearch(searchStaff: string, forComp?: number) {
    let compId: number = getCompanyId(forComp);

    if (!compId) {
      return;
    }
    try {
      const res = await UserServices.getStaffList({
        company: compId.toString(),
        smart_search: searchStaff,
        rows: appConstObj.record_per_page_1000,
      });
      const respData = get("data.data", res);
      if (respData.length) {
        setServedByList(respData);
      }
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error.message,
        type: messageTypeArr.error,
      });
    }
  }

  function handleFormChanges(data: any) {
    let form = updateIn(formData, [data.path], data.value);
    setFormData(form);
  }

  const handleDateChange = (date: any, frmIndex: any) => {
    let frmObj = { ...formData };
    frmObj[frmIndex] = date;
    setFormData({ ...frmObj });
  };

  function getDurationOptions(forD: string) {
    return orderDayTypeObj[forD as keyof typeof orderDayTypeObj].daysOptions;
  }

  function init() {
    try {
      setDurationList(getDurationOptions(tabViewSelected.toString()));
    } catch (error) {
      console.log(error);
    }
  }

  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    init();
  }, [tabViewSelected]);

  async function handleCountryChange(countryId: number) {
    try {
      const stateRes = await HelperServices.getAllValidStates({
        country: countryId,
      });
      setStateList(get("data.data", stateRes));
      resetSelectionFor("state");
      handleFormChanges({ path: "country", value: countryId });
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error.message,
        type: messageTypeArr.error,
      });
    }
  }

  async function handleStateChange(stateId: number) {
    try {
      const res = await HelperServices.getAllValidCities({ state: stateId });
      setCityList(res.data.data);
      resetSelectionFor("city");
      handleFormChanges({ path: "state", value: stateId });
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error.message,
        type: messageTypeArr.error,
      });
    }
  }

  async function handleCityChange(cityId: number) {
    try {
      const res = await HelperServices.getAllValidAreas({ city: cityId });
      setAreaList(res.data.data);
      resetSelectionFor("area");
      handleFormChanges({ path: "city", value: cityId });
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error.message,
        type: messageTypeArr.error,
      });
    }
  }

  async function handleAreaChange(areaId: number) {
    try {
      const res = await HelperServices.getAllValidBuildings({ area: areaId });
      setBuildingList(res.data.data);
      resetSelectionFor("building");
      handleFormChanges({ path: "area", value: areaId });
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error.message,
        type: messageTypeArr.error,
      });
    }
  }

  function clearSearch() {
    resetSelectionFor("country");
    clearOrderDates();
    onClear();
    setFormData(initSearchFormData);
  }

  function resetSelectionFor(resetFor: string) {
    let resetData: Object = {};
    switch (resetFor) {
      case "country":
        resetData = {
          country: "",
          state: "",
          city: "",
          area: "",
          building: "",
        };
        setStateList([]);
        setCityList([]);
        setAreaList([]);
        setBuildingList([]);
        break;
      case "state":
        resetData = {
          state: "",
          city: "",
          area: "",
          building: "",
        };
        setCityList([]);
        setAreaList([]);
        setBuildingList([]);
        break;
      case "city":
        resetData = {
          city: "",
          area: "",
          building: "",
        };
        setAreaList([]);
        setBuildingList([]);
        break;
      case "area":
        resetData = {
          area: "",
          building: "",
        };
        setBuildingList([]);
        break;
      case "building":
        resetData = {
          building: "",
        };
        break;
    }
    let newFormData = {
      ...formData,
      ...resetData,
    };
    setFormData(newFormData);
  }

  const handleRescheduledCheck = (value: any, checked: any) => {
    let form = updateIn(
      formData,
      ["is_order_rescheduled"],
      checked ? value : "",
    );
    setFormData(form);
  };

  return (
    <div>
      <div className="row mb-2 mb-hide">
        <ButtonToolbar>
          <Button onClick={handleSearchToggle}>{t("ADVANCE_SEARCH")}</Button>
        </ButtonToolbar>
      </div>
      {showFilter && (
        <div className="lbl-blk mb-3 content">
          <Form
            formValue={formData}
            onChange={(data: any) => {
              let form = { ...formData, ...data };
              setFormData(form);
            }}
            layout="inline"
          >
            <FlexboxGrid justify="space-between">
              <FlexboxGrid.Item>
                <Form.Group controlId="customer_name">
                  <Form.ControlLabel>
                    {t("CUSTOMER")} {t("NAME")}
                  </Form.ControlLabel>
                  <Form.Control name="customer_name" />
                </Form.Group>
              </FlexboxGrid.Item>
              <FlexboxGrid.Item>
                <Form.Group controlId="customer_phone">
                  <Form.ControlLabel>
                    {t("CUSTOMER")} {t("PHONE")}
                  </Form.ControlLabel>
                  <Form.Control name="customer_phone" />
                </Form.Group>
              </FlexboxGrid.Item>
              <FlexboxGrid.Item>
                <Form.Group controlId="item_name">
                  <Form.ControlLabel>
                    {t("ITEM")} {t("NAME")}
                  </Form.ControlLabel>
                  <Form.Control name="item_name" />
                </Form.Group>
              </FlexboxGrid.Item>
              <FlexboxGrid.Item>
                <Form.Group controlId="order_serve_at">
                  <Form.ControlLabel>
                    {t("ORDER")} {t("SERVE")} {t("AT")}
                  </Form.ControlLabel>
                  <SelectPicker
                    data={orderServeAtObjArr}
                    valueKey="value"
                    labelKey="label"
                    onSelect={(value, item) => {
                      handleFormChanges({ path: "order_serve_at", value });
                    }}
                    onClean={(e: any) => {
                      handleFormChanges({
                        path: "order_serve_at",
                        value: "",
                      });
                    }}
                    name="order_serve_at"
                    value={formData.order_serve_at}
                  />
                </Form.Group>
              </FlexboxGrid.Item>
              <FlexboxGrid.Item>
                <Form.Group controlId="order_assigned_to">
                  <Form.ControlLabel>
                    {t("ORDER")} {t("ASSIGNED")} {t("TO")}
                  </Form.ControlLabel>
                  <SelectPicker
                    data={assignedToList}
                    onOpen={() => assignedToSearch("")}
                    onSearch={(e: any) => {
                      if (e) {
                        assignedToSearch(e);
                      }
                    }}
                    valueKey="id"
                    labelKey="name"
                    onSelect={(value, item) => {
                      handleFormChanges({ path: "order_assigned_to", value });
                    }}
                    onClean={(e: any) => {
                      handleFormChanges({
                        path: "order_assigned_to",
                        value: "",
                      });
                    }}
                    name="order_assigned_to"
                    value={formData?.order_assigned_to}
                  />
                </Form.Group>
              </FlexboxGrid.Item>
              <FlexboxGrid.Item>
                <Form.Group controlId="served_by">
                  <Form.ControlLabel>
                    {t("SERVED")} {t("BY")}
                  </Form.ControlLabel>
                  <SelectPicker
                    data={servedByList}
                    onOpen={() => servedBySearch("")}
                    onSearch={(e: any) => {
                      if (e) {
                        servedBySearch(e);
                      }
                    }}
                    valueKey="id"
                    labelKey="name"
                    onSelect={(value, item) => {
                      handleFormChanges({ path: "served_by", value });
                    }}
                    onClean={(e: any) => {
                      handleFormChanges({
                        path: "served_by",
                        value: "",
                      });
                    }}
                    name="served_by"
                    value={formData?.served_by}
                  />
                </Form.Group>
              </FlexboxGrid.Item>
              <FlexboxGrid.Item>
                <Form.Group controlId="date_interval_type_val">
                  <Form.ControlLabel>
                    {t("DATE")} {t("INTERVAL")}
                  </Form.ControlLabel>
                  <SelectPicker
                    data={durationList}
                    valueKey="value"
                    labelKey="label"
                    onSelect={(value, item) => {
                      setOrderDates(value);
                      handleFormChanges({
                        path: "date_interval_type_val",
                        value,
                      });
                    }}
                    onClean={(e: any) => {
                      handleFormChanges({
                        path: "date_interval_type_val",
                        value: "",
                      });
                    }}
                    name="date_interval_type_val"
                    value={formData?.date_interval_type_val}
                  />
                </Form.Group>
              </FlexboxGrid.Item>
              <FlexboxGrid.Item>
                <Form.Group controlId="service_date_from">
                  <Form.ControlLabel>
                    {t("SERVICE")} {t("DATE")} {t("FROM")}
                  </Form.ControlLabel>
                  <DatePicker
                    value={formData?.service_date_from}
                    format={dateFormatObj.calendar_d_m_y}
                    name="service_date_from"
                    id="service_date_from"
                    onChange={(e: any) => {
                      handleDateChange(e, "service_date_from");
                    }}
                  />
                </Form.Group>
              </FlexboxGrid.Item>
              <FlexboxGrid.Item>
                <Form.Group controlId="service_date_to">
                  <Form.ControlLabel>
                    {t("SERVICE")} {t("DATE")} {t("TO")}
                  </Form.ControlLabel>
                  <DatePicker
                    value={formData?.service_date_to}
                    format={dateFormatObj.calendar_d_m_y}
                    name="service_date_to"
                    onChange={(e: any) => {
                      handleDateChange(e, "service_date_to");
                    }}
                  />
                </Form.Group>
              </FlexboxGrid.Item>

              <FlexboxGrid.Item>
                <Form.Group controlId="service_starts_at">
                  <Form.ControlLabel>
                    {t("SERVICE")} {t("TIME")}
                  </Form.ControlLabel>
                  <SelectPicker
                    data={dayTimeObjArr}
                    valueKey="value"
                    labelKey="label"
                    onSelect={(value, item) => {
                      handleFormChanges({ path: "service_starts_at", value });
                    }}
                    onClean={(e: any) => {
                      handleFormChanges({
                        path: "service_starts_at",
                        value: "",
                      });
                    }}
                    name="service_starts_at"
                    value={formData?.service_starts_at}
                  />
                </Form.Group>
              </FlexboxGrid.Item>
              <FlexboxGrid.Item>
                <Form.Group controlId="order_status">
                  <Form.ControlLabel>{t("STATUS")}</Form.ControlLabel>
                  <SelectPicker
                    data={orderStatusObjArr}
                    valueKey="value"
                    labelKey="label"
                    onSelect={(value, item) => {
                      handleFormChanges({ path: "order_status", value });
                    }}
                    onClean={(e: any) => {
                      handleFormChanges({ path: "order_status", value: "" });
                    }}
                    name="order_status"
                    value={formData?.order_status}
                  />
                </Form.Group>
              </FlexboxGrid.Item>
              <FlexboxGrid.Item>
                <Form.Group controlId="is_order_rescheduled">
                  <Form.ControlLabel>
                    {t("RESCHEDULED")} {t("ORDER")}
                  </Form.ControlLabel>
                  <Checkbox
                    name="is_order_rescheduled"
                    value={1}
                    onChange={handleRescheduledCheck}
                    checked={formData.is_order_rescheduled == "" ? false : true}
                  />
                </Form.Group>
              </FlexboxGrid.Item>
              <FlexboxGrid.Item>
                <Form.Group controlId="store">
                  <Form.ControlLabel>{t("STORE")}</Form.ControlLabel>
                  <SelectPicker
                    data={storeList}
                    onOpen={() => storeSearch("")}
                    onSearch={(e: any) => {
                      if (e) {
                        storeSearch(e);
                      }
                    }}
                    valueKey="id"
                    labelKey="store_name"
                    onSelect={(value, item) => {
                      handleFormChanges({ path: "store", value });
                    }}
                    onClean={(e: any) => {
                      handleFormChanges({ path: "store", value: "" });
                    }}
                    name="store"
                    value={formData?.store}
                  />
                </Form.Group>
              </FlexboxGrid.Item>
              <FlexboxGrid.Item>
                <Form.Group controlId="payment_mode">
                  <Form.ControlLabel>
                    {t("PAYMENT")} {t("MODE")}
                  </Form.ControlLabel>
                  <SelectPicker
                    data={paymentModeObjArr}
                    valueKey="value"
                    labelKey="label"
                    onSelect={(value, item) => {
                      handleFormChanges({ path: "payment_mode", value });
                    }}
                    onClean={(e: any) => {
                      handleFormChanges({ path: "payment_mode", value: "" });
                    }}
                    name="payment_mode"
                    value={formData?.payment_mode}
                  />
                </Form.Group>
              </FlexboxGrid.Item>
            </FlexboxGrid>
            <FlexboxGrid justify="space-between">
              <FlexboxGrid.Item>
                <Form.Group controlId="order_value">
                  <Form.ControlLabel>
                    {t("ORDER")} {t("VALUE")}
                  </Form.ControlLabel>
                  <SelectPicker
                    data={operatorsObjArr}
                    valueKey="value"
                    labelKey="label"
                    onSelect={(value, item) => {
                      handleFormChanges({ path: "order_value_op", value });
                    }}
                    onClean={(e: any) => {
                      handleFormChanges({
                        path: "order_value_op",
                        value: "",
                      });
                    }}
                    name="order_value_op"
                    value={formData.order_value_op}
                    defaultValue={operatorsObjArr[0].value}
                  />
                  <Form.Control name="order_value" />
                </Form.Group>
              </FlexboxGrid.Item>
              <FlexboxGrid.Item>
                <Form.Group controlId="discount_percent">
                  <Form.ControlLabel>
                    {t("DISCOUNT")} {t("%")}
                  </Form.ControlLabel>
                  <SelectPicker
                    data={operatorsObjArr}
                    valueKey="value"
                    labelKey="label"
                    onSelect={(value, item) => {
                      handleFormChanges({ path: "discount_percent_op", value });
                    }}
                    onClean={(e: any) => {
                      handleFormChanges({
                        path: "discount_percent_op",
                        value: "",
                      });
                    }}
                    name="discount_percent_op"
                    value={formData.discount_percent_op}
                    defaultValue={operatorsObjArr[0].value}
                  />
                  <Form.Control name="discount_percent" />
                </Form.Group>
              </FlexboxGrid.Item>
              <FlexboxGrid.Item>
                <Form.Group controlId="service_rating">
                  <Form.ControlLabel>
                    {t("SERVICE")} {t("RATING")}
                  </Form.ControlLabel>
                  <SelectPicker
                    data={operatorsObjArr}
                    valueKey="value"
                    labelKey="label"
                    onSelect={(value, item) => {
                      handleFormChanges({ path: "service_rating_op", value });
                    }}
                    onClean={(e: any) => {
                      handleFormChanges({
                        path: "service_rating_op",
                        value: "",
                      });
                    }}
                    name="service_rating_op"
                    value={formData.service_rating_op}
                    defaultValue={operatorsObjArr[0].value}
                  />
                  <Form.Control name="service_rating" />
                </Form.Group>
              </FlexboxGrid.Item>
            </FlexboxGrid>
            <FlexboxGrid justify="space-between">
              <FlexboxGrid.Item>
                <Form.Group controlId="country">
                  <Form.ControlLabel>{t("Country")}</Form.ControlLabel>
                  <SelectPicker
                    data={countryList}
                    valueKey="id"
                    labelKey="country_name"
                    onSelect={(value, item) => {
                      handleCountryChange(value);
                    }}
                    onClean={(e: any) => {
                      resetSelectionFor("country");
                    }}
                    name="country"
                    value={formData?.country}
                  />
                </Form.Group>
              </FlexboxGrid.Item>
              <FlexboxGrid.Item>
                <Form.Group controlId="state">
                  <Form.ControlLabel>{t("STATE")}</Form.ControlLabel>
                  <SelectPicker
                    data={stateList}
                    valueKey="id"
                    labelKey="state_name"
                    onSelect={(value, item) => {
                      handleStateChange(value);
                    }}
                    onClean={(e: any) => {
                      resetSelectionFor("state");
                    }}
                    name="state"
                    value={formData?.state}
                  />
                </Form.Group>
              </FlexboxGrid.Item>
              <FlexboxGrid.Item>
                <Form.Group controlId="city">
                  <Form.ControlLabel>{t("CITY")}</Form.ControlLabel>
                  <SelectPicker
                    data={cityList}
                    valueKey="id"
                    labelKey="city_name"
                    onSelect={(value, item) => {
                      handleCityChange(value);
                    }}
                    onClean={(e: any) => {
                      resetSelectionFor("city");
                    }}
                    name="city"
                    value={formData?.city}
                  />
                </Form.Group>
              </FlexboxGrid.Item>
              <FlexboxGrid.Item>
                <Form.Group controlId="area">
                  <Form.ControlLabel>{t("AREA")}</Form.ControlLabel>
                  <SelectPicker
                    data={areaList}
                    valueKey="id"
                    labelKey="area_name"
                    onSelect={(value, item) => {
                      handleAreaChange(value);
                    }}
                    onClean={(e: any) => {
                      resetSelectionFor("area");
                    }}
                    name="area"
                    value={formData?.area}
                  />
                </Form.Group>
              </FlexboxGrid.Item>
              <FlexboxGrid.Item>
                <Form.Group controlId="building">
                  <Form.ControlLabel>{t("BUILDING")}</Form.ControlLabel>
                  <SelectPicker
                    data={buildingList}
                    valueKey="id"
                    labelKey="building_name"
                    onSelect={(value, item) => {
                      handleFormChanges({ path: "building", value });
                    }}
                    onClean={(e: any) => {
                      resetSelectionFor("building");
                    }}
                    name="building"
                    value={formData?.building}
                  />
                </Form.Group>
              </FlexboxGrid.Item>
              <FlexboxGrid.Item>
                <Form.Group controlId="pincode">
                  <Form.ControlLabel>{t("PINCODE")}</Form.ControlLabel>
                  <Form.Control name="pincode" />
                </Form.Group>
              </FlexboxGrid.Item>
            </FlexboxGrid>

            <div className="filter-btn">
              <Button onClick={() => clearSearch()}>{t("CLEAR_FILTER")}</Button>
              <Button onClick={() => onSubmit(formData)}>{t("Search")}</Button>
            </div>
          </Form>
        </div>
      )}
    </div>
  );
}
